import { BRANCH_FILTER_STATUSES } from './branchFilterUi'
import { BranchStatus } from './types'

/** Opened = active and locked. Archived and deleted stay out until the user asks. */
export const OPENED_BRANCH_STATUSES: BranchStatus[] = ['ACTIVE', 'LOCKED']

export type BranchTreeLink = {
	id: string
	protectedBranch?: boolean
	sourceBranchId: string | null
	status: BranchStatus
}

export type BranchTreeFilter = {
	protectedOnly: boolean
	statuses: BranchStatus[]
}

export type BranchTreeSession = {
	customized: boolean
	expandedIds: string[]
	protectedOnly: boolean
	statuses: BranchStatus[]
}

export type BranchTreeBySystem = Record<string, BranchTreeSession>

export const branchMatchesFilter = (
	branch: BranchTreeLink,
	filter: BranchTreeFilter,
) => {
	if (!filter.statuses.includes(branch.status)) return false
	if (filter.protectedOnly && !branch.protectedBranch) return false
	return true
}

const walkAncestors = (
	byId: Map<string, BranchTreeLink>,
	branchId: string | null,
	visit: (branch: BranchTreeLink) => void,
) => {
	const seen = new Set<string>()
	let current = branchId ? byId.get(branchId) : undefined
	while (current && !seen.has(current.id)) {
		seen.add(current.id)
		visit(current)
		current = current.sourceBranchId
			? byId.get(current.sourceBranchId)
			: undefined
	}
}

/** Ancestors of the branch, root first. The branch itself stays collapsed. */
export const branchPathExpandedIds = (
	branches: BranchTreeLink[],
	branchId: string | null,
): string[] => {
	if (!branchId) return []
	const byId = new Map(branches.map((branch) => [branch.id, branch]))
	const path: string[] = []
	walkAncestors(byId, branchId, (branch) => {
		if (branch.id !== branchId) path.push(branch.id)
	})
	return path.reverse()
}

export const mergeExpandedIds = (saved: string[], pathIds: string[]) => [
	...new Set([...saved, ...pathIds]),
]

export const branchTreeVisibility = (
	branches: BranchTreeLink[],
	filter: BranchTreeFilter,
	activeBranchId: string | null,
) => {
	const byId = new Map(branches.map((branch) => [branch.id, branch]))
	const visibleIds = new Set<string>()
	const include = (branchId: string | null) => {
		walkAncestors(byId, branchId, (branch) => {
			visibleIds.add(branch.id)
		})
	}

	branches.forEach((branch) => {
		if (branchMatchesFilter(branch, filter)) include(branch.id)
	})
	if (activeBranchId && byId.has(activeBranchId)) include(activeBranchId)

	const dimmedIds = new Set<string>()
	visibleIds.forEach((id) => {
		const branch = byId.get(id)
		if (branch && !branchMatchesFilter(branch, filter)) dimmedIds.add(id)
	})

	return { dimmedIds, visibleIds }
}

const normalizeSession = (value: unknown): BranchTreeSession | null => {
	if (!value || typeof value !== 'object') return null
	const session = value as Partial<BranchTreeSession>
	const statuses = Array.isArray(session.statuses)
		? session.statuses.filter((status) => BRANCH_FILTER_STATUSES.includes(status))
		: OPENED_BRANCH_STATUSES
	return {
		customized: session.customized === true,
		expandedIds: Array.isArray(session.expandedIds)
			? session.expandedIds.filter((id) => typeof id === 'string')
			: [],
		protectedOnly: session.protectedOnly === true,
		statuses,
	}
}

export const normalizeBranchTreeBySystem = (
	value: unknown,
	fallback: BranchTreeBySystem = {},
): BranchTreeBySystem => {
	if (!value || typeof value !== 'object') return fallback
	const next: BranchTreeBySystem = {}
	Object.entries(value as Record<string, unknown>).forEach(([systemId, raw]) => {
		if (!systemId) return
		const session = normalizeSession(raw)
		if (session) next[systemId] = session
	})
	return next
}
